/**
 * **Close out Day 1 — for the crew** — `docs/visit-page.md` §27.4, Board 18 D
 * (sheet 562; on a phone Board 16's sheet from the bottom).
 *
 * While the crew app cannot close out a day (`crew.closeout` off), the office
 * does it for the crew, into the crew app's own records — opened from the
 * rail's **Close out** and the day sheet's **Close out for the crew** (Board
 * 18 F). *Entered by Jen Hollis for Marco Diaz.*; **How it reached the
 * office**; each line loaded for the day — *Loaded · Used · Wasted ·
 * Returned* — and *Both add up …* (`postCloseout`'s own rule: red, and **Close
 * out Day 1** disabled, while they do not); **Equipment hours** per unit (per
 * type when there are no units, §27.5); **A word on the day**; what it posts
 * (internal). `CrewPort.closeOutDay`'s stand-in writes `visit_closeouts`,
 * `closeout_lines` and `equipment_usage`, then posts labor and equipment
 * (§6.6) — exactly what the iPad will call.
 *
 * Whether the lines add up, and what it posts, is the server's
 * (`POST …/closeout/preview`, asked as the boxes change) — the page adds up
 * nothing.
 */
import { useEffect, useState } from 'react'
import type {
  CloseoutForm, CloseoutInput, CloseoutPreview, ReachedBy, VisitRead,
} from '@rowted/shared'
import { REACHED_BY } from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { request } from '../lib/api.js'
import { usePhone } from '../lib/use-narrow.js'
import { useVisitTab, useVisitWrite, visitPath } from './api.js'
import { VisitGlyph } from './glyphs.js'
import { VisitButton, VisitCard, VisitTile, VISIT_LAYER } from './sheet.js'
import { FieldLabel, SLOT, fill } from './fields.js'
import { InternalChip } from './internal-chip.js'
import { CLOSE_OUT_FOR_CREW as WORDS, COMMON, MATERIALS } from './copy.js'
import { dayText, money } from './format.js'

/** What is typed in a box — kept as text so a box can be empty while somebody types. */
type Typed = Record<string, string>

export function CloseOutForCrewSheet({ visitId, read, day, phone: forced, layer = VISIT_LAYER, onClose, onClosedOut }: {
  visitId: string
  read: VisitRead
  /** The day's index — *Close out Day 1*. */
  day: number
  phone?: boolean
  layer?: number
  onClose: () => void
  /** After **Close out Day 1** — the page has the fresh Overview already. */
  onClosedOut?: () => void
}) {
  const windowPhone = usePhone()
  const phone = forced ?? windowPhone
  const form = useVisitTab<CloseoutForm>(visitId, `/days/${day}/closeout`)
  const write = useVisitWrite(visitId)
  const data = form.data

  const [reachedBy, setReachedBy] = useState<ReachedBy>('call')
  const [typed, setTyped] = useState<Typed>({})
  const [note, setNote] = useState('')
  const [preview, setPreview] = useState<CloseoutPreview | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /* The server's draft, once: used = loaded, nothing wasted or returned, the planned hours. */
  useEffect(() => {
    if (!data) return
    setReachedBy(data.draft.reachedBy)
    setNote(data.draft.note)
    const next: Typed = {}
    for (const line of data.draft.lines) {
      next[`${line.lineId}:used`] = String(line.used)
      next[`${line.lineId}:wasted`] = String(line.wasted)
      next[`${line.lineId}:returned`] = String(line.returned)
    }
    for (const row of data.draft.equipment) next[`${row.unitId ?? row.typeId}:hours`] = row.hours.toFixed(1)
    setTyped(next)
  }, [data])

  const body = data ? bodyOf(data, typed, reachedBy, note) : null

  /* Whether it adds up is asked of the server as the boxes change. */
  const key = body ? JSON.stringify({ lines: body.lines, equipment: body.equipment }) : ''
  useEffect(() => {
    if (!body) { setPreview(null); return }
    let live = true
    const timer = window.setTimeout(() => {
      request<CloseoutPreview>(visitPath(visitId, `/days/${day}/closeout/preview`), { method: 'POST', body })
        .then((answer) => { if (live) setPreview(answer) })
        .catch((cause: unknown) => { if (live) setError(cause instanceof Error ? cause.message : String(cause)) })
    }, 200)
    return () => { live = false; window.clearTimeout(timer) }
  }, [visitId, day, key])

  const ready = Boolean(body && preview?.allAddUp) && !busy

  async function closeOut() {
    if (!ready || !body) return
    setBusy(true)
    setError(null)
    try {
      await write('POST', `/days/${day}/closeout`, body, { tab: 'overview' })
      onClosedOut?.()
      onClose()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setBusy(false)
    }
  }

  const lead = data?.lead ?? null
  const size = phone ? 'phone' : 'card'
  const internal = preview?.internal ?? data?.internal

  return (
    <VisitCard
      width={562}
      phone={phone}
      title={WORDS.title(String(day))}
      sub={data ? WORDS.sub(dayText(data.day.date, 'date'), data.crew?.name ?? '', lead?.name ?? '') : undefined}
      mark={<VisitTile glyph="clipboard" tone="teal" size={36} />}
      onClose={onClose}
      layer={layer}
      note={data && lead ? WORDS.footer(String(day), data.you.name, lead.name) : undefined}
      asDrawn="vp-18 D"
      footer={(
        <>
          <VisitButton size={size} onClick={onClose}>{WORDS.cancel}</VisitButton>
          <VisitButton size={size} tone="primary" glyph="check" disabled={!ready} onClick={() => void closeOut()}>
            {WORDS.submit(String(day))}
          </VisitButton>
        </>
      )}
    >
      {data && (
        <div className="flex flex-col gap-3.5">
          {lead && (
            <div className="flex items-start gap-[9px] rounded-[10px] border border-info-line bg-info-bg px-3 py-2.5
                            text-[12.5px] leading-[1.45] text-info">
              <VisitGlyph name="info" size={15} stroke={2} />
              <div><b>{WORDS.enteredBy(data.you.name, lead.name)}</b> {WORDS.band(lead.firstName)}</div>
            </div>
          )}

          <div className="min-w-0">
            <FieldLabel>{WORDS.reachedOffice}</FieldLabel>
            <div role="radiogroup" aria-label={WORDS.reachedOffice} className="flex flex-wrap gap-1.5">
              {REACHED_BY.map((how) => {
                const on = how === reachedBy
                return (
                  <button
                    key={how}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    onClick={() => setReachedBy(how)}
                    className={cn('inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border font-semibold',
                      phone ? 'h-11 px-3.5 text-[13px]' : 'h-[30px] px-2.5 text-[12px]',
                      on ? 'border-accent-line bg-accent-soft text-accent-dark' : 'border-line bg-surface text-text hover:bg-hover')}
                  >
                    {on && <VisitGlyph name="check" size={12} stroke={2.6} />}
                    {WORDS.reachedBy[how]}
                  </button>
                )
              })}
            </div>
          </div>

          {data.lines.length > 0 && (
            <div className="overflow-hidden rounded-[10px] border border-line">
              <div className={cn(LINE_GRID, 'bg-surface-alt py-2 text-[10.5px] font-semibold uppercase tracking-[.06em] text-text-faint')}>
                <span>{WORDS.loadedFor(String(day))}</span>
                <span className="text-right">{WORDS.head.loaded}</span>
                <span>{WORDS.head.used}</span>
                <span>{WORDS.head.wasted}</span>
                <span>{WORDS.head.returned}</span>
              </div>
              {data.lines.map((line) => (
                <div key={line.lineId} className={cn(LINE_GRID, 'border-t border-line-faint py-2')}>
                  <div className="flex min-w-0 flex-col">
                    <b className="truncate text-[12.5px]">
                      {line.names.detail ? COMMON.dot(line.names.name, line.names.detail) : line.names.name}
                    </b>
                    <span className="truncate text-[11px] text-text-muted">
                      {line.source === 'yard' && line.unit ? WORDS.fromYard(line.unit.one) : MATERIALS.source[line.source]}
                    </span>
                  </div>
                  <span className="text-right text-[12.5px] font-semibold">
                    {line.unit ? COMMON.qty(String(line.loaded), line.loaded === 1 ? line.unit.one : line.unit.many) : String(line.loaded)}
                  </span>
                  {(['used', 'wasted', 'returned'] as const).map((part) => (
                    <NumberBox
                      key={part}
                      label={WORDS.head[part]}
                      value={typed[`${line.lineId}:${part}`] ?? ''}
                      onChange={(value) => setTyped((was) => ({ ...was, [`${line.lineId}:${part}`]: value }))}
                    />
                  ))}
                </div>
              ))}
              {preview && (
                <div className={cn('flex items-center gap-2 border-t border-line-faint px-3 py-2 text-[11.5px]',
                  preview.allAddUp ? 'bg-green-band-bg text-accent-dark' : 'bg-danger-bg text-danger')}>
                  <VisitGlyph name={preview.allAddUp ? 'checkCircle' : 'warn'} size={14} stroke={2} />
                  {WORDS.addsUp}
                </div>
              )}
            </div>
          )}

          {data.equipment.length > 0 && (
            <div className="overflow-hidden rounded-[10px] border border-line">
              <div className="bg-surface-alt px-3 py-2 text-[10.5px] font-semibold uppercase tracking-[.06em] text-text-faint">
                {WORDS.equipmentHours}
              </div>
              {data.equipment.map((row, index) => {
                const id = `${row.unit?.id ?? row.type.id}:hours`
                return (
                  <div key={id} className={cn('flex items-center gap-2.5 px-3 py-[7px]', index > 0 && 'border-t border-line-faint')}>
                    {row.unit && (
                      <span className="inline-flex h-6 shrink-0 items-center rounded-[6px] border border-line px-[7px] text-[12px] font-bold">
                        {row.unit.tag}
                      </span>
                    )}
                    <span className="min-w-0 flex-1 truncate text-[12.5px]">{row.type.name}</span>
                    <NumberBox
                      label={row.unit?.tag ?? row.type.name}
                      value={typed[id] ?? ''}
                      onChange={(value) => setTyped((was) => ({ ...was, [id]: value }))}
                      unit={WORDS.unit}
                      width={78}
                    />
                  </div>
                )
              })}
            </div>
          )}

          <div className="min-w-0">
            <FieldLabel htmlFor="closeout-word">{WORDS.aWord}</FieldLabel>
            <textarea
              id="closeout-word"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              className="block h-[58px] w-full resize-none tabular-nums rounded-button border border-line bg-surface px-3 py-2.5 text-[13px]
                         leading-normal text-text focus:border-accent-line focus:outline-none"
            />
          </div>

          {internal && (
            <div className="flex items-center gap-2.5 rounded-[10px] border border-line bg-surface-internal px-3 py-2.5 text-[12.5px] text-text-body">
              <InternalChip />
              {/* Each run its own item of the row, 10 apart, as the board lays the sentence out. */}
              {fill(WORDS.posts(String(day), SLOT, SLOT, read.reference),
                <b key="labor" className="text-text">{money(internal.labor)}</b>,
                <b key="equipment" className="text-text">{money(internal.equipment)}</b>)}
            </div>
          )}

          {error && <p className="m-0 text-[12.5px] text-danger">{error}</p>}
        </div>
      )}
    </VisitCard>
  )
}

const LINE_GRID = 'grid grid-cols-[1.6fr_74px_74px_74px_82px] items-center gap-2.5 px-3'

/** A 30px number box, right-aligned (Board 18 D), with its unit after it where the board draws one. */
function NumberBox({ label, value, onChange, unit, width }: {
  label: string
  value: string
  onChange: (value: string) => void
  unit?: string
  width?: number
}) {
  return (
    <span
      className="flex h-[30px] items-center rounded-button border border-line bg-surface px-[11px] text-[12.5px] text-text
                 focus-within:border-accent-line"
      style={width ? { width } : undefined}
    >
      <input
        aria-label={label}
        inputMode="decimal"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="min-w-0 flex-1 bg-transparent text-right tabular-nums outline-none"
      />
      {unit && <span className="ml-1.5 text-[12px] text-text-faint">{unit}</span>}
    </span>
  )
}

const NUMBER = /^\d+(\.\d+)?$/

/** The form as typed, as the schema takes it — or null while a box is empty or not a number. */
function bodyOf(data: CloseoutForm, typed: Typed, reachedBy: ReachedBy, note: string): CloseoutInput | null {
  const read = (key: string): number | null => {
    const value = (typed[key] ?? '').trim()
    return NUMBER.test(value) ? Number(value) : null
  }
  const lines: CloseoutInput['lines'] = []
  for (const line of data.lines) {
    const used = read(`${line.lineId}:used`)
    const wasted = read(`${line.lineId}:wasted`)
    const returned = read(`${line.lineId}:returned`)
    if (used === null || wasted === null || returned === null) return null
    lines.push({ lineId: line.lineId, used, wasted, returned })
  }
  const equipment: CloseoutInput['equipment'] = []
  for (const row of data.equipment) {
    const hours = read(`${row.unit?.id ?? row.type.id}:hours`)
    if (hours === null) return null
    equipment.push({ unitId: row.unit?.id ?? null, typeId: row.type.id, hours })
  }
  return { reachedBy, lines, equipment, note: note.trim() }
}
